import { Card } from "@/components/ui/Card";
import type { CookbookMeal } from "@/lib/models";

interface CookbookMealCardProps {
  meal: CookbookMeal;
  onTap: (meal: CookbookMeal) => void;
}

/**
 * Grid card for the member-facing Cookbook page. Mirrors iOS
 * `Views/Cookbook/MealCard.swift` shape: hero photo on top, meal name
 * + meta (prep time, servings, optional calories) below. Tap opens
 * the recipe detail sheet — selection / servings happen on iOS via a
 * separate "Add to grocery list" CTA which the web surface omits
 * (no equivalent generateGroceryListFromCookbook flow on web yet).
 */
export function CookbookMealCard({ meal, onTap }: CookbookMealCardProps) {
  const heroUrl = meal.hero_image_url ?? null;
  const dietaryTags = meal.dietary_tags ?? [];
  const prepTime = meal.prep_time_minutes ?? 0;
  const servings = meal.servings ?? 0;
  const calories = meal.calories_per_serving;

  return (
    <Card padding="p-0">
      <button
        type="button"
        onClick={() => onTap(meal)}
        className="block w-full text-left overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent-color)]"
      >
        <div className="aspect-[4/3] w-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          {heroUrl ? (
            // Plain <img> here on purpose — DownloadableImage adds a hover
            // download chip that fights the card-tap affordance. Detail
            // sheet uses DownloadableImage for the full hero.
            <img
              src={heroUrl}
              alt={meal.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-2xl text-gray-300 dark:text-gray-500">
              {"\u{1F374}"}
            </div>
          )}
        </div>
        <div className="p-2 space-y-1">
          <h3 className="font-semibold text-xs leading-snug line-clamp-2">{meal.name}</h3>
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400">
            {prepTime > 0 && <span>{prepTime}m</span>}
            {servings > 0 && <span>· {servings} sv</span>}
          </div>
          {dietaryTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {dietaryTags.slice(0, 2).map((tag) => (
                <span
                  key={tag}
                  className="text-[9px] px-1 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 capitalize"
                >
                  {tag.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          )}
        </div>
      </button>
    </Card>
  );
}
